import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useUserStore } from "@/store/user";
import { defineStore, storeToRefs } from "pinia";

// 查询键常量
export const USER_RIGHTS_QUERY_KEYS = {
  userRights: (userId: string) => {
    return ["userRights", String(userId)];
  }
};

// 一级路由列表 - 需要更新用户权益的路由
const TOP_LEVEL_ROUTES = ["workbench", "model", "image", "space", "spaceNew", "group"] as const;
// 指定需要立即刷新的路由（如权益相关页面）
const immediateRefreshRoutes = ["power", "space", "spaceNew", "workspace"];
/**
 * 用户权益管理 Hook
 * 基于 vue-query 提供智能缓存、自动重试和状态管理
 */
export const userRightStore = defineStore("userRightStore", () => {
  const userStore = useUserStore();
  const { isLogin, userInfo } = storeToRefs(userStore);

  // 用户权益查询
  const { isLoading, error, refetch, isFetching } = useQuery({
    queryKey: computed(() => USER_RIGHTS_QUERY_KEYS.userRights(userInfo.value.id || "0")),
    queryFn: () => Promise.all([userStore.queryUserRights(), userStore.queryUserInfo()]),
    enabled: computed(() => isLogin.value && !isNaN(Number(userInfo.value?.id))),
    staleTime: Infinity,
    refetchOnWindowFocus: false, // 窗口聚焦时不自动刷新
    refetchOnMount: false // 组件挂载时不自动刷新
  });

  // 手动刷新用户权益
  const refreshUserRights = async () => {
    if (!isLogin.value || !userInfo.value?.id) {
      console.warn("用户未登录，无法刷新权益信息");
      return;
    }
    return await refetch();
  };

  return {
    // 方法
    refreshUserRights,

    // 状态
    isLoading,
    isFetching,
    error
  };
});

/**
 * 用户权益自动管理 Hook
 * 在应用级别使用，自动处理路由切换时的权益更新
 */
export function useUserRightsAutoManager() {
  const userRightsHook = userRightStore();
  const route = useRoute();
  const queryClient = useQueryClient();
  const userStore = useUserStore();
  const { isLogin, userInfo } = storeToRefs(userStore);
  // 监听一级路由变化，智能更新用户权益

  watch(
    () => route.name,
    (newRouteName) => {
      if (!isLogin.value || !userInfo.value?.id) return;

      // 检查是否为一级路由
      const isTopLevelRoute = route.matched.length === 1;
      const isTargetRoute = TOP_LEVEL_ROUTES.includes(newRouteName as any);

      if (isTopLevelRoute || isTargetRoute) {
        const needImmediateRefresh = immediateRefreshRoutes.includes(newRouteName as string);

        if (needImmediateRefresh) {
          // 指定路由立即刷新
          userRightsHook.refreshUserRights();
        } else {
          // 其他一级路由检查缓存时间（3分钟）
          const queryData = queryClient.getQueryData(USER_RIGHTS_QUERY_KEYS.userRights(userInfo.value.id));
          const queryState = queryClient.getQueryState(USER_RIGHTS_QUERY_KEYS.userRights(userInfo.value.id));

          const now = Date.now();
          const lastFetchTime = queryState?.dataUpdatedAt || 0;
          const isStale = now - lastFetchTime > 3 * 60 * 1000; // 3分钟

          // 如果数据不存在或已过期，则刷新
          if (!queryData || isStale) {
            userRightsHook.refreshUserRights();
          }
        }
      }
    },
    { immediate: false }
  );
  // 返回只读的状态和方法
  return {
    refreshUserRights: userRightsHook.refreshUserRights
  };
}
